import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import i18n from 'i18next';
import { Trans } from 'react-i18next';
import { css } from '@emotion/core';

import * as clientErrTypes from 'config/clientErrorTypes';
import { generateApiErrObj } from 'client/hooks/useClientErrorHandler';

import useScrollElementOnMount from 'client/hooks/useScrollElementOnMount';
import PointIcon from 'components/PointSystem/PointIcon';
import Popup from 'client/components/Popup';
import DialogDeductSuccess from 'client/components/dialogVerification/dialogDeductPoint/DialogDeductSuccess';
import DialogDeductFailed from 'client/components/dialogVerification/dialogDeductPoint/DialogDeductFailed';
import DialogPointNotEnough from 'client/components/dialogVerification/dialogDeductPoint/DialogPointNotEnough';
import { Typography } from '@material-ui/core';
import useClientErrorHandler from 'client/hooks/useClientErrorHandler';
import { loginTypeMappingPointRoutesParam } from 'config/loginTypes';
import { deductPointByPromo, getMemberPointTotalByMemberApi } from 'api/point/client';
import { setNeedReducePoint } from 'client/redux/modules/userEntryState';
import { isErrMemberPointsNotEnough } from 'client/utils/errorObjectChecker';
import useGetPromoPointPoolInfo from 'client/hooks/useGetPromoPointPoolInfo';
import useGetPartnerOrSponsorPointIcon, {
    processed_type
} from 'client/hooks/useGetPartnerOrSponsorPointIcon';

export default function DialogDeductPoint({ open, onClose = () => {}, onApiError = () => {} }) {
    const dispatch = useDispatch();
    const bindRef = useScrollElementOnMount();
    const errHandler = useClientErrorHandler();
    const { uuid: promoUUID } = useSelector(s => s.promo);
    const { loginMethod, transactionWithdrawQuantity } = useSelector(s => s.settingPromo);
    const { qualifyWithdrawUuid } = useSelector(s => s.settingPointSystems);
    const { currentEntryUUID } = useSelector(s => s.userEntryState);
    const [isRequesting, setIsRequesting] = useState(true);
    const [points, setPoints] = useState('');
    const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
    const [openFailedDialog, setOpenFailedDialog] = useState(false);
    const pointNotEnough = points < transactionWithdrawQuantity;

    useEffect(() => {
        const pointFetch = async () => {
            setIsRequesting(true);
            try {
                const [remainPointResp] = await Promise.all([
                    getMemberPointTotalByMemberApi({
                        pointPoolUuid: qualifyWithdrawUuid,
                        loginType: loginTypeMappingPointRoutesParam[loginMethod]
                    })
                ]);
                if (remainPointResp.httpStatus >= 400) {
                    throw generateApiErrObj(
                        remainPointResp,
                        clientErrTypes.GET_MEMBER_POINT_FAILED
                    );
                } else {
                    setPoints(remainPointResp.payload.points);
                }
            } catch (err) {
                errHandler({ err });
            } finally {
                setIsRequesting(false);
            }
        };
        pointFetch();
    }, [errHandler, loginMethod, open, qualifyWithdrawUuid]);

    async function handleSubmit() {
        setIsRequesting(true);
        try {
            const resp = await deductPointByPromo({ promoUUID, currentEntryUUID });
            if (resp.httpStatus === 200) {
                setOpenSuccessDialog(true);
                setTimeout(() => {
                    onClose();
                    dispatch(setNeedReducePoint(false));
                }, 2000);
            } else if (isErrMemberPointsNotEnough(resp)) {
                setOpenFailedDialog(true);
                onClose();
            } else {
                throw generateApiErrObj(
                    resp,
                    clientErrTypes.TRANSACTION_WITHDRAW_QUANTITY_IS_WRONG
                );
            }
        } catch (error) {
            onApiError(error);
        } finally {
            setIsRequesting(false);
        }
    }

    return (
        <>
            <DialogDeductSuccess
                open={openSuccessDialog}
                transactionWithdrawQuantity={transactionWithdrawQuantity}
            />
            <DialogDeductFailed
                open={openFailedDialog}
                onClose={() => setOpenFailedDialog(false)}
                handleSubmit={handleSubmit}
            />
            {pointNotEnough && !isRequesting ? (
                <DialogPointNotEnough
                    isOpen={open && !openSuccessDialog}
                    onClose={onClose}
                    points={points}
                    transactionWithdrawQuantity={transactionWithdrawQuantity}
                />
            ) : (
                <Popup
                    isOpen={open && !openSuccessDialog}
                    title={i18n.t('label.qualification_confirmation')}
                    onClose={onClose}
                    startButton={i18n.t('btn.close')}
                    endButton={i18n.t('btn.confirm_deduction')}
                    onConfirm={handleSubmit}
                    disableBackdropClick
                    endButtonDisable={isRequesting || pointNotEnough}
                >
                    <div css={dialogContentStyle} ref={bindRef}>
                        <Typography variant="subtitle2" css={descStyle}>
                            <Trans
                                i18nKey={i18n.t('label.can_use')}
                                components={
                                    <PointSystemPoints pointCount={points} pointSize={20} />
                                }
                            />
                        </Typography>
                        <Typography variant="subtitle2" css={descStyle}>
                            <Trans
                                i18nKey={i18n.t('label.are_you_sure_join_promo')}
                                components={
                                    <PointSystemPoints
                                        pointCount={transactionWithdrawQuantity}
                                        pointSize={14}
                                    />
                                }
                            />
                        </Typography>
                        <Typography variant="subtitle2">
                            {i18n.t('label.non_refundable_after_exchange')}
                        </Typography>
                    </div>
                </Popup>
            )}
        </>
    );
}

const dialogContentStyle = css`
    padding: 16px;
    text-align: center;
`;

const descStyle = css`
    display: flex;
    justify-content: center;
    align-items: center;
`;

export function PointSystemPoints({
    pointCount,
    pointSize = 24,
    style,
    needShowPoint = true,
    pointPoolUuid,
    pointPoolType = processed_type.DEDUCTION
}) {
    const { qualifyWithdrawUuid } = useSelector(s => s.settingPointSystems);
    const getPromoPointPoolInfo = useGetPromoPointPoolInfo();
    const pointPoolInfo = getPromoPointPoolInfo(pointPoolUuid ?? qualifyWithdrawUuid);
    const pointIconStyle = pointPoolInfo?.settings?.pointIconStyle || {};
    const { partnerPointIconUrl, sponsorPointIconUrl } = useGetPartnerOrSponsorPointIcon(
        pointPoolType
    );

    return (
        <div css={[pointSystemPointsStyle, style]}>
            <PointIcon
                pointIconStyle={{
                    ...pointIconStyle,
                    bgColor: pointIconStyle?.iconColor,
                    colorText: '#FFF'
                }}
                size={pointSize}
                partnerPointIconUrl={partnerPointIconUrl}
                sponsorPointIconUrl={sponsorPointIconUrl}
            />

            {needShowPoint && (
                <Typography
                    variant="subtitle2"
                    css={css`
                        font-size: ${pointSize}px;
                        padding-left: 4px;
                    `}
                >
                    {pointCount}
                </Typography>
            )}
        </div>
    );
}

const pointSystemPointsStyle = css`
    display: flex;
    justify-content: start;
    align-items: center;
    padding: 0 4px;
`;
